import { ConditionalRender } from '@/components/Helpers/ComponentRender/ConditionalRender';
import { EventTracker } from '@/utils';
import { ConnectionCard } from '@design-system/Cards/ConnectionCard';
import { StandardContainer } from '@design-system/StandardContainer';
import { useKitType } from '@hooks/useKittype';
import { useIsProUser } from '@hooks/useIsProUser';
import { SkeletonView } from '@screens/HealthConnections/HealthConnectionsContainer';
import { HealthDetailsSectionHeader } from '@screens/HealthPages/HealthDetailsSectionHeader';
import { HealthL4ImproveMyScoreInfo } from '@screens/HealthPages/HealthL4ImproveMyScoreInfo';
import { DoctorsNotes } from '@screens/LevelTwoDetail/DoctorsNotes';
import DoctorsNoteShimmer from '@screens/LevelTwoDetail/DoctorsNoteShimmer';
import ImproveMyScoreShimmer from '@screens/LevelTwoDetail/ImproveMyScoreShimmer';
import {
  useGetDoctorsNotesV2,
  useGetImproveScoreSummaryV2,
  useGetL4ConnectionListV2,
  useGetL4DiseaseRiskListV2,
  useGetScoreDetailsV2,
} from '@viome/viome-base/src/usecase/Health/HealthQuery';
import { useNavigate, useParams } from 'react-router-dom';

interface L4Connection {
  id: number;
  name: string;
  connection_type: string;
  connection_type_color: string;
  no_of_connections: number | null;
}

interface L4ConnectionsSectionViewProps {
  title: string;
  description: string;
  connections?: L4Connection[];
  isLoading: boolean;
  isError: boolean;
  isProUser: boolean;
  scoreTitle: string;
  onCardClick: (connectionName: string) => void;
}

const L4ConnectionsSectionView = ({
  title,
  description,
  connections,
  isLoading,
  isError,
  isProUser,
  scoreTitle,
  onCardClick,
}: L4ConnectionsSectionViewProps) => (
  <ConditionalRender
    conditions={[
      {
        when: isLoading,
        render: (
          <section className={isProUser ? 'mb-10' : 'mb-24'} data-testid="healthL4Details-healthConnectionsLoadingSection">
            <SkeletonView data-testid="healthL4Details-healthConnectionsSkeleton" />
          </section>
        ),
      },
      { when: isError, render: null },
      { when: !connections?.length, render: null },
    ]}
    otherwise={
      <div className={`mt-12 ${isProUser ? 'mb-10' : 'mb-24'}`} data-testid="healthL4Details-healthConnections">
        <div className="flex flex-col" data-testid="healthL4Details-healthConnectionsHeader">
          <div className="dark typography-h1 text-primary mb-2 flex" data-testid="healthL4Details-healthConnectionsTitle">
            {title}
          </div>
          <div className="flex dark typography-body text-secondary mb-6 lg:w-[35rem]">
            {description}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="healthL4Details-healthConnectionsGrid">
          {connections?.map((connection, index) => (
            <div key={`${connection.name}-${connection.id}`} data-testid={`healthL4Details-connectionCard-${index}`}>
              <ConnectionCard
                name={connection.name}
                noOfConnections={connection.no_of_connections}
                connectionType={connection.connection_type}
                connectionTypeColor={connection.connection_type_color}
                isProUser={isProUser}
                onClick={() => {
                  EventTracker.trackEvent(
                    'health',
                    `${scoreTitle.replace(/\s+/g, '')}Details_healthConnections_${connection.name.replace(/\s+/g, '')}_reported`,
                  );
                  onCardClick(connection.name);
                }}
                data-testid={`healthL4Details-connectionCard-${connection.name.replace(/\s+/g, '-').toLowerCase()}`}
              />
            </div>
          ))}
        </div>
      </div>
    }
  />
);

const HealthConnectionsL4Section = ({
  scoreId,
  scoreTitle,
  onCardClick,
}: {
  scoreId: number;
  scoreTitle: string;
  onCardClick: (connectionName: string) => void;
}) => {
  const { data, isLoading, isError } = useGetL4ConnectionListV2(scoreId);
  return (
    <L4ConnectionsSectionView
      title="Health Connections"
      description="We’ve found a connection between this pathway score and a condition. This condition may be one you self-reported or an Associated Health Risk identified through our research."
      connections={data}
      isLoading={isLoading}
      isError={isError}
      isProUser={false}
      scoreTitle={scoreTitle}
      onCardClick={onCardClick}
    />
  );
};

const DiseaseRiskL4Section = ({
  scoreId,
  scoreTitle,
  onCardClick,
}: {
  scoreId: number;
  scoreTitle: string;
  onCardClick: (connectionName: string) => void;
}) => {
  const { data, isLoading, isError } = useGetL4DiseaseRiskListV2(scoreId);
  return (
    <L4ConnectionsSectionView
      title="Disease Risk"
      description="The pathway score contributed to the elevated disease risk. This condition may be one you self-reported."
      connections={data}
      isLoading={isLoading}
      isError={isError}
      isProUser
      scoreTitle={scoreTitle}
      onCardClick={onCardClick}
    />
  );
};

export const HealthL4DetailsPage = () => {
  const { id: scoreId } = useParams();
  const navigate = useNavigate();
  const kitType = useKitType();
  const isOHIKitTypeUser = kitType === 'OHI';
  const isProUser = useIsProUser();

  const {
    data: l4ScoreDetail,
    isLoading: isL4ScoreDetailAPILoading,
    isError: isL4ScoreDetailAPIError,
  } = useGetScoreDetailsV2('L4', Number(scoreId));

  const {
    data: doctorNotes,
    isLoading: isDoctorsNoteLoading,
    isError: isDoctorsNoteError,
  } = useGetDoctorsNotesV2('L4', Number(scoreId));

  const {
    data: improveMyScore,
    isLoading: isImproveMyScoreLoading,
    isError: isImproveMyScoreError,
  } = useGetImproveScoreSummaryV2(Number(scoreId));

  const navigateToConditionsPage = (title: string) => {
    return navigate(`/health/connections/${encodeURIComponent(title)}`);
  };

  const renderDoctorsNotWithBg = () => {
    if (!doctorNotes) return;
    return (
      <section
        className="bg-black bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 30%, rgba(0, 0, 0, 1) 100%), url('https://cdn-st.stage.azure.viome.com/viome-cdn/media/app/v2/png/home/image_for_doctors_notes.png')`,
        }}
        data-testid="healthL4Details-doctorsNoteSection"
      >
        <StandardContainer>
          <DoctorsNotes notes={doctorNotes} data-testid="healthL4Details-doctorsNotes" />
        </StandardContainer>
      </section>
    );
  };

  const renderImproveMyScore = () => {
    return (
      <section className='mb-6' data-testid="healthL4Details-improveScoreSection">
        {improveMyScore && (
          <HealthL4ImproveMyScoreInfo improveMyScoreInfo={improveMyScore} data-testid="healthL4Details-improveScoreInfo" />
        )}
      </section>
    );
  };

  const scoreTitle = l4ScoreDetail?.title ?? '';

  // PRO -> Disease Risk; consumer (non-OHI) -> Health Connections; OHI -> neither.
  // Mirrors mfe: which component mounts decides which API fires, no dual-hook enabled flags.
  const connectionsSection = isProUser ? (
    <DiseaseRiskL4Section
      scoreId={Number(scoreId)}
      scoreTitle={scoreTitle}
      onCardClick={navigateToConditionsPage}
    />
  ) : !isOHIKitTypeUser ? (
    <HealthConnectionsL4Section
      scoreId={Number(scoreId)}
      scoreTitle={scoreTitle}
      onCardClick={navigateToConditionsPage}
    />
  ) : null;

  return (
    <div className="bg-black h-full text-primary w-full dark !max-w-full" data-testid="healthL4Details-page">
      <HealthDetailsSectionHeader
        scoreDetail={l4ScoreDetail}
        isScoreDetailsAPILoading={isL4ScoreDetailAPILoading}
        scoreId={Number(scoreId)}
        isL4ScoreDetailAPIError={isL4ScoreDetailAPIError}
        // Disease Risk is reordered above "Related Health Zone" for PRO users;
        // Health Connections keeps its original position for everyone else.
        topSection={
          isProUser ? (
            <section data-testid="healthL4Details-diseaseRiskSection">
              <StandardContainer>{connectionsSection}</StandardContainer>
            </section>
          ) : undefined
        }
        data-testid="healthL4Details-header"
      />
      <ConditionalRender
        // Doctors note conditional rendering
        conditions={[
          { when: isDoctorsNoteLoading, render: <DoctorsNoteShimmer data-testid="healthL4Details-doctorsNoteShimmer" /> },
          { when: isDoctorsNoteError, render: null },
        ]}
        otherwise={renderDoctorsNotWithBg()}
      />
      <StandardContainer data-testid="healthL4Details-contentContainer">
        <ConditionalRender
          // Improve my score conditional rendering
          conditions={[
            {
              when: isImproveMyScoreLoading,
              render: <ImproveMyScoreShimmer data-testid="healthL4Details-improveScoreShimmer" />,
            },
            { when: isImproveMyScoreError, render: null },
          ]}
          otherwise={renderImproveMyScore()}
        />
        {!isProUser && connectionsSection}
      </StandardContainer>
    </div>
  );
};
